import { Trans } from '@lingui/react/macro';
import type { ShoppingCartCarWithSalesItems, ShoppingCartRecord } from '@navio/nsdk';
import { CarSpec, SelectedSalesItemsCar } from 'noddi-types';
import { compareLicensePlates, formatCarLicensePlate, formatCurrencyAmount } from 'noddi-util';
import { CarImage, getShortAddressName, NavioTireSeasonChip, useBookingCars } from 'noddi-web';
import { useMembershipProgramImages } from '../../hooks/useMembershipProgramImages';
import { useMobileBookingCustomer } from '../../hooks/useMobileBookingCustomer';
import { getCarName } from '../../utils/carName';
import { toCheckoutDiscountRows } from '../../utils/checkoutDiscounts';
import { getCommonTranslations } from '../../utils/commonTranslations';
import { CheckoutDiscountRow } from './CheckoutDiscountRow';

type PriceSummaryProps = {
  shoppingCart: ShoppingCartRecord;
  selectedSalesItemsCars?: SelectedSalesItemsCar[];
  hideSummary?: boolean;
};

export const ShoppingCartSummary = ({ shoppingCart, selectedSalesItemsCars, hideSummary }: PriceSummaryProps) => {
  const { cars, couponDiscounts, membershipDiscounts, totalPrice, deliveryWindowFee } = shoppingCart;
  const { userGroupId } = useMobileBookingCustomer();
  const { imageByDiscountId } = useMembershipProgramImages(userGroupId);
  // The cart echoes plates, not images; the draft kept each car's image when it was picked.
  const bookingCars = useBookingCars();
  const discountRows = toCheckoutDiscountRows({
    couponDiscounts,
    membershipDiscounts,
    imageByDiscountId
  });
  const couponRows = discountRows.filter((row) => row.kind === 'coupon');
  const membershipRows = discountRows.filter((row) => row.kind === 'membership');

  return (
    <div className='flex flex-col gap-3'>
      {cars.map((car) => (
        <ShoppingCartCars
          key={car.licensePlate?.number ?? car.licensePlate}
          car={car}
          bookingCar={bookingCars.find((bookingCar) => compareLicensePlates(bookingCar.licensePlate, car.licensePlate))}
          selectedSalesItemsCars={selectedSalesItemsCars}
        />
      ))}
      {couponRows.length > 0 && (
        <div className='flex flex-col gap-1 border-t pt-3'>
          <h6 className='font-semibold'>
            <Trans>Discounts</Trans>
          </h6>
          {couponRows.map((row) => (
            <CheckoutDiscountRow key={row.key} label={row.label} amount={row.amount} />
          ))}
        </div>
      )}
      {membershipRows.length > 0 && (
        <div className='flex flex-col gap-1'>
          <h6 className='font-semibold'>
            <Trans>Membership benefits</Trans>
          </h6>
          {membershipRows.map((row) => (
            <CheckoutDiscountRow key={row.key} label={row.label} amount={row.amount} imageUrl={row.imageUrl} />
          ))}
        </div>
      )}
      {!hideSummary && (
        <div className='flex flex-col gap-1 border-t pt-3'>
          {!!deliveryWindowFee?.amount && (
            <div className='flex justify-between'>
              <span>
                <Trans>Delivery</Trans>
              </span>
              <span>{formatCurrencyAmount({ money: deliveryWindowFee })}</span>
            </div>
          )}
          <div className='flex justify-between font-semibold'>
            <span>
              <Trans>Total sum including VAT</Trans>
            </span>
            <span>{formatCurrencyAmount({ money: totalPrice })}</span>
          </div>
        </div>
      )}
    </div>
  );
};

const ShoppingCartCars = ({
  car,
  bookingCar,
  selectedSalesItemsCars
}: {
  car: ShoppingCartCarWithSalesItems;
  bookingCar: CarSpec | undefined;
  selectedSalesItemsCars?: SelectedSalesItemsCar[];
}) => {
  // Find tire hotel location for this car
  const tireHotelInfo = selectedSalesItemsCars?.find(
    (item) =>
      compareLicensePlates(item.licensePlate, car.licensePlate) &&
      (item.metaData?.selectedTireHotelLocation || item.metaData?.selectedTireHotelPickUpAddress)
  );

  const incomingTireSeasonItem = selectedSalesItemsCars?.find(
    (item) => compareLicensePlates(item.licensePlate, car.licensePlate) && item.metaData?.incomingTireSeason
  );

  return (
    <div className='flex flex-col gap-2'>
      <div className='flex items-center gap-2'>
        {/* No draft car (a stale cart) still keeps the slot, so names line up across cars. */}
        <CarImage car={bookingCar ?? {}} alt='' size='thumbnail' />
        <h2 className='min-w-0 font-semibold'>{getCarName({ car })}</h2>
        <span className='ml-auto text-sm font-medium tracking-wide tabular-nums'>{formatCarLicensePlate(car)}</span>
      </div>

      <div className='flex flex-col gap-1'>
        {car.salesItems.map((item) => (
          <div key={`${car.licensePlate.number} - ${item.name}`} className='flex flex-col gap-1'>
            <div className='flex justify-between gap-4'>
              <span>{item.name}</span>
              <span>{formatCurrencyAmount({ money: item.price.priceGross })}</span>
            </div>

            {/* Show tire hotel location if this sales item has tire hotel pickup */}
            {tireHotelInfo?.salesItemId === item.salesItemId &&
              (tireHotelInfo?.metaData?.selectedTireHotelPickUpAddress ||
                tireHotelInfo?.metaData?.selectedTireHotelLocation) && (
                <div className='flex justify-between gap-4'>
                  <span>
                    <Trans>Pick up wheels at</Trans>
                  </span>
                  {tireHotelInfo?.metaData?.selectedTireHotelLocation?.name && (
                    <span className='text-right text-sm'>{tireHotelInfo.metaData.selectedTireHotelLocation.name}</span>
                  )}
                  {/* Fallback to address if no location is set */}
                  {tireHotelInfo?.metaData?.selectedTireHotelPickUpAddress &&
                    !tireHotelInfo?.metaData?.selectedTireHotelLocation?.name && (
                      <span className='text-right text-sm'>
                        {getShortAddressName(tireHotelInfo.metaData.selectedTireHotelPickUpAddress)}
                      </span>
                    )}
                </div>
              )}

            {incomingTireSeasonItem?.salesItemId === item.salesItemId && (
              <div className='flex items-center justify-between gap-4'>
                <span>
                  <Trans>Tires to storage</Trans>
                </span>
                <NavioTireSeasonChip
                  season={incomingTireSeasonItem.metaData?.incomingTireSeason}
                  size='sm'
                  translations={getCommonTranslations().seasonBadge}
                />
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};
